import type { Nullary, Unary } from '@vaaas/fpts/data';
import type { HandlerParameters, Handlers } from './rpc-container';
import type {
    Request as JSONRPCRequest,
    Response as JSONRPCResponse,
    MultiplexedResponse as MultiplexedJSONRPCREsponse,
} from './json-rpc-container';
import type { PromiseResult, Right } from '@vaaas/fpts/promise-result';
import { randint } from '@vaaas/fpts/maths';
import { I } from '@vaaas/fpts/combinator';
import { ofKV, pop } from '@vaaas/fpts/map';

type Config = {
    base_url: string;
    id_factory?: Nullary<number>;
    http?: Unary<Request, Promise<Response>>;
    batch_size?: number;
    next_tick?: Unary<VoidFunction, any>;
};

type Entry = [body: JSONRPCRequest['body'], f: Unary<any, void>]

type QueueLike = Entry[];

type Mapping = Map<number, Unary<any, void>>;

export const random_id = () => randint(0, Number.MAX_SAFE_INTEGER);

function pop_batch(queue: QueueLike, batch_size: number): QueueLike {
    const batch: Entry[] = [];
    for (let i = 0; i < batch_size && queue.length > 0; i++)
        batch.push(queue.shift()!);
    return batch;
}

const id_mapping = ofKV((x: Entry) => x[0].id)((x: Entry) => x[1]);

export const get_result = (x: JSONRPCResponse) => {
    if ('error' in x) {
        const e = new Error(x.error.message);
        (e as any).code = x.error.code;
        return e;
    } else
        return x.result;
};

const commit_response = (map: Mapping, x: JSONRPCResponse) => {
    const done = pop(x.id)(map);
    if (done) done(get_result(x));
};

export const make_json_rpc_request = (base_url: string, method: string, ...params: any[]) =>
    new Request(base_url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            jsonrpc: '2.0',
            id: random_id(),
            method,
            params,
        }),
    });

export function make_json_rpc_caller<H extends Handlers>({
    base_url,
    id_factory = random_id,
    http = fetch,
    batch_size = 10,
    next_tick = queueMicrotask,
}: Config) {
    const queue: QueueLike = [];
    let pending = false;

    function commit_requests() {
        const batch = pop_batch(queue, batch_size);
        if (!batch.length) return;
        const map = id_mapping(batch);
        pending = false;
        return http(new Request(base_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(batch.length === 1 ? batch[0]![0] : batch.map(x => x[0])),
        }))
            .then(x => x.json() as Promise<JSONRPCResponse | MultiplexedJSONRPCREsponse>)
            .then((x) => {
                if (x instanceof Error)
                    return x;
                else if (Array.isArray(x))
                    for (const y of x) commit_response(map, y);
                else
                    commit_response(map, x);
            })
            .catch(I)
            .finally(() => {
                for (const f of map.values())
                    f(new Error('Did not receive response'));
                if (queue.length)
                    commit_requests();
            });
    }

    return function rpc_factory<K extends keyof H & string>(name: K) {
        return function rpc(...xs: HandlerParameters<H[K]>): PromiseResult<Right<ReturnType<H[K]['procedure']>>> {
            return new Promise(done => {
                const body: JSONRPCRequest['body'] = {
                    jsonrpc: '2.0',
                    id: id_factory(),
                    method: name,
                    params: xs,
                };
                queue.push([body, done]);

                if (!pending) {
                    pending = true;
                    next_tick(() => commit_requests());
                }
            });
        };
    };
}
